import React, { useEffect, useRef, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { Theme } from '@theme'
import { Icon, Screen, SearchBar } from '@components'

import { Scope } from '@utils'
import ContactsFilter from '../components/ContactsFilter'
import { ContactsList } from '../../contacts/listingContacts/components'
import WebMap from './components/WebMap'
import { buildMapContactsFilter } from './mapContactsFilter'
import { usePaginateContacts, useUpdateProfile } from '@hooks'
import { getCurrentPosition } from '@utils'

const DEFAULT_REGION = {
  lat: 46.603354, // Centre de la France
  lng: 1.888334,
  latitudeDelta: 4,
  longitudeDelta: 4,
};

// Fixed width of the right-hand contacts panel, so the zoom slider can sit
// just to its left when it's open (and at the map's edge when collapsed).
const PANEL_WIDTH = 340;

export const MapScreen: React.FC = () => {
  const theme = useTheme()   
  const styles = getQRCodeStyles(theme)
  const navigation = useNavigation()

  const mapRef = useRef<any>(null)
  const [mapCenter, setMapCenter] = useState<[number, number]>([DEFAULT_REGION.lat, DEFAULT_REGION.lng])

  const [selectedContactId, setSelectedContactId] = useState<string>();
  const [scope, setScope] = useState<Scope[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [isCollapsed, setIsCollapsed] = useState(false)
  
  // "Fantôme" filter is a separate bonus set, like in the contacts picker:
  // - selected -> only ghost profiles (placed approximately by their city)
  // - not selected -> real contacts only (no ghosts)
  const { flatData: allContacts, isLoading: isLoadingContacts, hasNextPage: hasNextPageContacts, fetchNextPage: fetchNextPageContacts, isFetchingNextPage: isFetchingNextPageContacts } = usePaginateContacts({
    ...buildMapContactsFilter(scope),
    search: searchText,
  })
  const { mutate: updateProfileMutation } = useUpdateProfile()
  const nearByContacts = allContacts
    .filter((contact) => contact.latitude != null && contact.longitude != null)
    .map((contact) => ({ ...contact, lat: contact.latitude!, lng: contact.longitude! }))

  useEffect(() => {
    getCurrentPosition().then((coords) => {
      if (coords) {
        updateProfileMutation({ latitude: coords.latitude, longitude: coords.longitude, _silent: true })
        setMapCenter([coords.latitude, coords.longitude])
      }
    })
  }, [updateProfileMutation])

  const onContactClick = (contactId: string) => {
    setSelectedContactId(contactId);
    (navigation as any).navigate('Profile', { screen: 'DetailsGhostProfile', params: { profileId: contactId } });
  }

  const toggleModal = () => {
    setIsCollapsed(!isCollapsed)
  }

  return (
    <Screen noPaddingBottom noPaddingTop>
      <WebMap
        ref={mapRef}
        center={mapCenter}
        zoom={DEFAULT_REGION.latitudeDelta > 10 ? 6 : 13}
        style={styles.map}
        contacts={nearByContacts}
        onContactClick={onContactClick}
        rightOffset={isCollapsed ? 16 : PANEL_WIDTH + 16}
      />
      <View style={[styles.modal, !isCollapsed && { width: PANEL_WIDTH }]}>
        <View style={styles.closeButton}>
          <Icon name={isCollapsed? "arrowLeft" : "arrowRight"} size={16} color={theme.colors.lightGrey} onPress={toggleModal} />
        </View>
        {!isCollapsed && <View>
          <View style={styles.padding}>
            <View style={styles.searchBarContainer}><SearchBar value={searchText} onChangeText={setSearchText} /></View>
            <ContactsFilter
              scope={scope}
              onScopeChange={setScope}
            />
          </View>
          <ContactsList noRightSection contacts={nearByContacts} selectedContactId={selectedContactId}
            onContactPress={(contact) => {
              setSelectedContactId(contact.id);
              mapRef?.current?.zoomToContact(contact);
            }}
            isLoading={isLoadingContacts}
            hasNextPage={hasNextPageContacts}
            fetchNextPage={fetchNextPageContacts}
            isFetchingNextPage={isFetchingNextPageContacts}
          />
        </View>}
      </View>
    </Screen>
  )
}

const getQRCodeStyles = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: theme.spacing.xl,
      paddingHorizontal: theme.spacing.lg,
    },
    content: {
      flex: 1,
    },
    map: {
      flex: 1
    },
    marker: {
      width: 40,
      height: 40,
      borderRadius: 20,
      borderWidth: 2,
      borderColor: 'white',
      overflow: 'hidden'
    },
    modal: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      right: 0,
      backgroundColor: theme.colors.white,
      zIndex: 1000
    },
    closeButton: {
      position: 'absolute',
      top: theme.spacing.xs,
      left: -(15 + theme.spacing.lg),
      padding: theme.spacing.sm,
      paddingRight: theme.spacing.lg,
      borderTopLeftRadius: theme.border.extraLarge,
      borderBottomLeftRadius: theme.border.extraLarge,
      backgroundColor: theme.colors.white,
    },
    padding: {
      padding: theme.spacing.md,
    },
    searchBarContainer: {
      marginBottom: theme.spacing.md,
    },
    listContent: {
      height: 300,
      paddingTop: theme.spacing.lg,
      paddingBottom: theme.spacing.xxxl
    }
  })
